import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ICurrentUser, updateCurrentUser } from '@query'
import { ColumnKey, COLUMNS, DEFAULT_COLUMNS } from './columns'

const AGENCY_DEFAULT_COLUMNS: ColumnKey[] = ['automation', 'deadline', 'drafts', 'luca']

const resolveDefaultColumns = (user?: ICurrentUser): ColumnKey[] =>
  user?.is_agency_user && !user.ui_preferences?.businesses_table?.columns?.length
    ? AGENCY_DEFAULT_COLUMNS
    : DEFAULT_COLUMNS

/**
 * The enabled-column configuration, kept on the user profile
 * (`ui_preferences.businesses_table.columns`). Toggles apply optimistically
 * and persist with a short debounce so rapid toggling causes one PATCH.
 * Saves are serialized — at most one PATCH is in flight, and a snapshot
 * that accumulates meanwhile is sent when it settles — so an older request
 * can never land on the server after a newer one. A failed save rolls the
 * view back to the server state, and a pending save is flushed on unmount
 * instead of being lost.
 */
export const useColumnPrefs = (user?: ICurrentUser) => {
  const queryClient = useQueryClient()
  const saveTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)
  const pendingColumns = useRef<ColumnKey[] | null>(null)
  const inFlightColumns = useRef<ColumnKey[] | null>(null)

  // Drop unknown keys so stale saved values cannot poison later PATCHes.
  const rawSaved = user?.ui_preferences?.businesses_table?.columns
  const savedColumns = useMemo(
    () =>
      Array.isArray(rawSaved)
        ? rawSaved.filter((key): key is ColumnKey => key in COLUMNS)
        : undefined,
    [rawSaved]
  )
  const [localColumns, setLocalColumns] = useState<ColumnKey[] | null>(null)

  const enabledColumns = useMemo(
    () => localColumns ?? savedColumns ?? resolveDefaultColumns(user),
    [localColumns, savedColumns, user]
  )

  const { mutate } = useMutation({
    mutationFn: (columns: ColumnKey[]) =>
      updateCurrentUser(undefined, {
        ui_preferences: { businesses_table: { columns } }
      }),
    // Only settle the pending state when THIS exact snapshot finished —
    // an older save must not clear or roll back a newer change.
    onSuccess: (_data, columns) => {
      if (pendingColumns.current === columns) pendingColumns.current = null
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
    // The optimistic view must not pretend a failed save succeeded; fall
    // back to the last server-confirmed configuration.
    onError: (_error, columns) => {
      if (pendingColumns.current === columns) {
        pendingColumns.current = null
        setLocalColumns(null)
      }
    },
    onSettled: (_data, _error, columns) => {
      inFlightColumns.current = null
      // A newer snapshot accumulated while this save was in flight; send it
      // now so the latest state is what lands last on the server.
      if (pendingColumns.current && pendingColumns.current !== columns) {
        persistRef.current(pendingColumns.current)
      }
    }
  })

  const persist = useCallback(
    (columns: ColumnKey[]) => {
      // Serialize: while a save is in flight, just keep the pending
      // snapshot — onSettled flushes it once the current request finishes.
      if (inFlightColumns.current) return
      inFlightColumns.current = columns
      mutate(columns)
    },
    [mutate]
  )

  const persistRef = useRef(persist)
  persistRef.current = persist
  const mutateRef = useRef(mutate)
  mutateRef.current = mutate

  useEffect(
    () => () => {
      // Flush a still-debounced save on unmount so the change is not lost —
      // directly, bypassing the in-flight guard, because the mutation
      // callbacks (and so the queued flush) stop firing after unmount.
      clearTimeout(saveTimeout.current)
      if (pendingColumns.current && pendingColumns.current !== inFlightColumns.current) {
        mutateRef.current(pendingColumns.current)
      }
    },
    []
  )

  const toggleColumn = useCallback(
    (key: ColumnKey) => {
      const next = enabledColumns.includes(key)
        ? enabledColumns.filter(column => column !== key)
        : [...enabledColumns, key]
      setLocalColumns(next)
      pendingColumns.current = next

      clearTimeout(saveTimeout.current)
      saveTimeout.current = setTimeout(() => persist(next), 600)
    },
    [enabledColumns, persist]
  )

  return { enabledColumns, toggleColumn }
}
